import type { Injector } from '@angular/core';
import { inject, runInInjectionContext } from '@angular/core';
import { MonitoringService } from '@quipex/core/logging';
import { assertInjector, ClientError } from '@quipex/core/utils';
import type { ErrorCodeSpec } from './error-codes';

type AngularArtifactType =
    | 'component'
    | 'directive'
    | 'pipe'
    | 'service'
    | 'unknown';

function sanitizeUrnPart(value: string): string {
    // First convert to kebab-case
    return value
        .trim() // Remove leading and trailing whitespace
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .replace(/([a-z])([A-Z])/g, '$1-$2') // Insert hyphen for camelCase boundaries
        .toLowerCase() // Lowercase before stripping invalids so capitals are preserved as letters
        .replace(/[^a-z0-9\-_.]/g, '-') // Replace all non-alphanumeric (except hyphen/underscore/dot) with a hyphen
        .replace(/-+/g, '-'); // Replace multiple hyphens with a single hyphen
}

export type BuildUrnParts = readonly [AngularArtifactType, ...string[]];

export function buildUrn(parts: BuildUrnParts): string {
    const sanitized = parts.map(sanitizeUrnPart).filter((p) => p.length > 0);
    return `urn:problem:qpx:${sanitized.join(':')}`;
}

export function buildLoggingEventName(urn?: string | BuildUrnParts): string {
    if (!urn) return 'ClientError';

    if (typeof urn === 'string') {
        // Strip prefix up to and including 'qpx:' if present
        const idx = urn.indexOf('qpx:');
        const suffix = idx >= 0 ? urn.slice(idx + 4) : urn;
        return `ClientError:${suffix}`;
    }

    const sanitized = urn.map(sanitizeUrnPart).filter((p) => p.length > 0);
    return `ClientError:${sanitized.join(':')}`;
}

/**
 * Create a factory for minting ClientError instances with consistent logging.
 * - Adds URN (urn:problem:qpx:...), instance (current URL)
 * - Logs once via MonitoringService with optional logging context
 * - Returns the ClientError (does not throw)
 *
 * Preferred usage mirrors other custom injection helpers (e.g., `injectDuplicateDocumentCheck`).
 *
 * @example
 * ```ts
 * // In a service or component
 * private readonly clientError = injectError();
 *
 * const err = this.clientError.create(DOC_UPLOAD_ERROR.PreparationFailed, {
 *   urnParts: ['service','document-upload','upload-key-documents'],
 *   context: { buildingId, fileCount: files.length },
 *   loggingContext: { scope: 'documents.upload.prep' },
 * });
 *
 * return throwError(() => err);
 * ```
 */
export function injectError(injector?: Injector): {
    create: (
        spec: ErrorCodeSpec,
        options: {
            urnParts: readonly [AngularArtifactType, ...string[]];
            name?: string;
            context?: Record<string, unknown>;
            loggingContext?: Record<string, unknown>;
            consoleErrorRestArgs?: readonly unknown[];
        }
    ) => ClientError;
} {
    const resolvedInjector = assertInjector(injectError, injector);

    const create = (
        spec: ErrorCodeSpec,
        options: {
            urnParts: readonly [AngularArtifactType, ...string[]];
            name?: string;
            context?: Record<string, unknown>;
            loggingContext?: Record<string, unknown>;
            consoleErrorRestArgs?: readonly unknown[];
        }
    ): ClientError => {
        return runInInjectionContext(resolvedInjector, () => {
            const monitoring = inject(MonitoringService);
            const instance = getHrefSafe();
            const type = buildUrn(options.urnParts);
            const err = new ClientError(spec, {
                type,
                instance,
                name: options?.name,
                context: options?.context,
            });
            const eventName = buildLoggingEventName(options.urnParts);
            monitoring.logEvent(eventName, {
                status: spec.status,
                title: spec.statusText,
                type,
                instance,
                ...(options?.loggingContext ?? {}),
                timestamp: new Date().toISOString(),
            });
            if (options?.consoleErrorRestArgs !== undefined) {
                console.error(eventName, ...options.consoleErrorRestArgs);
            } else {
                console.error(eventName, err, options?.loggingContext);
            }
            return err;
        });
    };

    return { create };
}

function getHrefSafe(): string | undefined {
    try {
        return window?.location?.href;
    } catch {
        return undefined;
    }
}
